import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import {
  AdminMoveRequest,
  adminGetUserBilling,
  adminMoveMember,
  adminPauseMember,
  adminPreviewMove,
  adminResumeMember,
  adminUndoPause,
} from "../../../api/apiEndpoints";
import { formatCurrency } from "../../../utils/partnerMetrics";
import MultiSelect from "./MultiSelect";
import { Button, FormField, Label, Row, SectionTitle, Select, StatusText } from "./styles";

const Box = styled.div<{ $tone?: "warn" | "error" }>`
  border: 1px solid ${({ $tone }) => ($tone === "error" ? "#fecaca" : $tone === "warn" ? "#fde68a" : "#e5e7eb")};
  background: ${({ $tone }) => ($tone === "error" ? "#fef2f2" : $tone === "warn" ? "#fffbeb" : "#fafafa")};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;

  & > span:first-child {
    color: #666;
    flex-shrink: 0;
  }

  & > span:last-child {
    text-align: right;
    font-weight: 600;
  }
`;

const Radio = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
  padding: 3px 0;
`;

const cents = (v?: number | null) => (v == null ? "—" : formatCurrency(v / 100));
const date = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const PLAN_OPTIONS: { value: AdminMoveRequest["plan"]; label: string; product?: string }[] = [
  { value: "auto", label: "Recommended (trial if eligible, otherwise upgrade)" },
  { value: "basic", label: "Trial", product: "basic" },
  { value: "premium", label: "Upgrade (monthly)", product: "premium" },
  { value: "premium_quarterly", label: "Upgrade (quarterly)", product: "premium_quarterly" },
  { value: "premium_yearly", label: "Upgrade (yearly)", product: "premium_yearly" },
];

type Status = { msg: string; ok: boolean } | null;

const errorMessage = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;

interface Props {
  userId: string;
  businesses: any[];
  onChanged: () => void;
}

const MemberMembership = ({ userId, businesses, onChanged }: Props) => {
  const [billing, setBilling] = useState<any | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);

  const [mode, setMode] = useState<AdminMoveRequest["mode"]>("next");
  const [businessId, setBusinessId] = useState("");
  const [plan, setPlan] = useState<AdminMoveRequest["plan"]>("auto");
  const [settlement, setSettlement] = useState<NonNullable<AdminMoveRequest["settlement"]>>("none");
  const [acknowledged, setAcknowledged] = useState(false);
  const [preview, setPreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveStatus, setMoveStatus] = useState<Status>(null);

  const [pauseDays, setPauseDays] = useState<7 | 14 | 30>(7);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseStatus, setPauseStatus] = useState<Status>(null);

  const loadBilling = useCallback(async () => {
    setBillingError(null);
    try {
      const res = await adminGetUserBilling(userId);
      setBilling(res.data);
    } catch (e: any) {
      setBilling(null);
      setBillingError(errorMessage(e, "Couldn't load billing from Stripe."));
    }
  }, [userId]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    setPreview(null);
    setSettlement("none");
    setAcknowledged(false);
  }, [mode, businessId, plan]);

  const studios = businesses
    .filter((b: any) => (b.products ?? []).length)
    .sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
  const studio = businesses.find((b: any) => b.businessId === businessId);
  const studioPlans = new Set((studio?.products ?? []).map((p: any) => p.name));
  const planOptions = PLAN_OPTIONS.filter((o) => !o.product || studioPlans.has(o.product));

  const request = (): AdminMoveRequest => ({ mode, businessId, plan, settlement, acknowledgeCheckIn: acknowledged });

  const runPreview = async () => {
    setPreviewLoading(true);
    setMoveStatus(null);
    try {
      const res = await adminPreviewMove(userId, { ...request(), settlement: "none" });
      setPreview(res.data);
    } catch (e: any) {
      setPreview(null);
      setMoveStatus({ msg: errorMessage(e, "Couldn't preview this move."), ok: false });
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmMove = async () => {
    setMoveLoading(true);
    setMoveStatus(null);
    try {
      await adminMoveMember(userId, request());
      setMoveStatus({ msg: "Done. Stripe and the database are updated.", ok: true });
      setPreview(null);
      setBusinessId("");
      await loadBilling();
      onChanged();
    } catch (e: any) {
      setMoveStatus({ msg: errorMessage(e, "The move failed. Nothing was changed."), ok: false });
    } finally {
      setMoveLoading(false);
    }
  };

  const runPause = async (action: "pause" | "undo" | "resume") => {
    const prompts = {
      pause: `Pause this membership for ${pauseDays} days? Their next renewal moves back ${pauseDays} days.`,
      undo: "Undo the scheduled pause? They'll renew on their original date.",
      resume: "Resume now? Their card is charged today and a new month starts.",
    };
    if (!window.confirm(prompts[action])) return;
    setPauseLoading(true);
    setPauseStatus(null);
    try {
      if (action === "pause") await adminPauseMember(userId, pauseDays);
      if (action === "undo") await adminUndoPause(userId);
      if (action === "resume") await adminResumeMember(userId);
      setPauseStatus({ msg: "Done. Stripe and the database are updated.", ok: true });
      await loadBilling();
      onChanged();
    } catch (e: any) {
      setPauseStatus({ msg: errorMessage(e, "That didn't work. Nothing was changed."), ok: false });
    } finally {
      setPauseLoading(false);
    }
  };

  const db = billing?.database;
  const stripe = billing?.stripe;
  const pausedUntil = db?.pausedUntil && new Date(db.pausedUntil) > new Date() ? db.pausedUntil : null;
  const s = preview?.settlement;
  const canConfirm =
    !!preview &&
    preview.blockers.length === 0 &&
    (!preview.checkedIn || acknowledged) &&
    !moveLoading;

  return (
    <>
      <SectionTitle style={{ fontSize: 14, marginBottom: 12 }}>Billing in Stripe</SectionTitle>
      {billingError ? (
        <Box $tone="error">{billingError}</Box>
      ) : !billing ? (
        <Box>Loading…</Box>
      ) : !stripe ? (
        <Box>No active subscription in Stripe.</Box>
      ) : (
        <>
          {billing.mismatch && (
            <Box $tone="error">
              Stripe and the database disagree about the next renewal. Stripe will renew them into{" "}
              <strong>{stripe.renewal ?? "nothing"}</strong>; the database says <strong>{db.next ?? "nothing"}</strong>.
              Stripe wins at renewal.
            </Box>
          )}
          <Box>
            <Line><span>Subscription</span><span>{stripe.status}</span></Line>
            <Line>
              <span>Next renewal</span>
              <span>
                {stripe.cancelsAtPeriodEnd
                  ? `Cancels ${date(stripe.renewsAt)}`
                  : `${stripe.renewal ?? "Unknown plan"} on ${date(stripe.renewsAt)}`}
              </span>
            </Line>
            <Line><span>Renewal price</span><span>{cents(stripe.renewalPriceCents)}</span></Line>
            <Line><span>Current plan (database)</span><span>{db.current ?? "—"}</span></Line>
            <Line><span>Checked in this period</span><span>{db.checkedIn ? "Yes" : "No"}</span></Line>
            {pausedUntil && <Line><span>Paused until</span><span>{date(pausedUntil)}</span></Line>}
            {db.scheduledCancelAt && <Line><span>Cancels on</span><span>{date(db.scheduledCancelAt)}</span></Line>}
            {stripe.creditCents > 0 && <Line><span>Account credit</span><span>{cents(stripe.creditCents)}</span></Line>}
          </Box>
        </>
      )}

      <SectionTitle style={{ fontSize: 14, marginTop: 24, marginBottom: 12 }}>Change gym</SectionTitle>
      <Row style={{ marginBottom: 8 }}>
        <Select value={mode} onChange={(e) => setMode(e.target.value as AdminMoveRequest["mode"])}>
          <option value="next">At their next renewal</option>
          <option value="current">Now (changes their current gym)</option>
        </Select>
      </Row>
      <FormField style={{ marginBottom: 8 }}>
        <Label>Studio</Label>
        <MultiSelect
          single
          options={studios.map((b: any) => ({ value: b.businessId, label: b.metro ? `${b.name} · ${b.metro}` : b.name }))}
          selected={businessId ? [businessId] : []}
          onChange={(ids) => setBusinessId(ids[0] ?? "")}
          allLabel="Select a studio…"
          noun="studios"
        />
      </FormField>
      {businessId && (
        <FormField style={{ marginBottom: 8 }}>
          <Label>Plan</Label>
          <Select value={plan} onChange={(e) => setPlan(e.target.value as AdminMoveRequest["plan"])}>
            {planOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FormField>
      )}
      <Row>
        <Button variant="secondary" onClick={runPreview} disabled={!businessId || previewLoading}>
          {previewLoading ? "Checking…" : "Preview change"}
        </Button>
      </Row>

      {preview && (
        <>
          {preview.blockers.map((b: string) => <Box key={b} $tone="error">{b}</Box>)}
          {preview.warnings.map((w: string) => <Box key={w} $tone="warn">{w}</Box>)}
          <Box>
            {mode === "current" && <Line><span>Current plan</span><span>{preview.current ?? "—"}</span></Line>}
            <Line><span>{mode === "current" ? "Moves to now" : "Renews into"}</span><span>{preview.target}</span></Line>
            <Line>
              <span>Trial at this studio</span>
              <span>
                {!preview.trial.available
                  ? "Not offered"
                  : preview.trial.eligible
                  ? "Eligible"
                  : `Not eligible (trial started ${date(preview.trial.lastStartedAt)})`}
              </span>
            </Line>
            {mode === "current" && <Line><span>Then renews into</span><span>{preview.renewal}</span></Line>}
            <Line><span>Renewal date</span><span>{date(preview.renewsAt)}</span></Line>
            <Line><span>Stripe will charge at renewal</span><span>{cents(preview.renewalPriceCents)}</span></Line>
          </Box>

          {s && s.differenceCents !== 0 && (
            <FormField style={{ marginBottom: 12 }}>
              <Label>
                {s.differenceCents > 0
                  ? `The new plan costs ${cents(s.differenceCents)} more this month`
                  : `The new plan costs ${cents(-s.differenceCents)} less this month`}
              </Label>
              <Radio>
                <input type="radio" checked={settlement === "none"} onChange={() => setSettlement("none")} />
                {s.differenceCents > 0 ? "Don't charge" : "No refund"}
              </Radio>
              {s.differenceCents > 0 ? (
                <Radio>
                  <input type="radio" checked={settlement === "charge"} onChange={() => setSettlement("charge")} />
                  Charge the difference now: {cents(s.chargeCents)}
                </Radio>
              ) : (
                <>
                  <Radio>
                    <input type="radio" checked={settlement === "credit"} onChange={() => setSettlement("credit")} />
                    Credit their next bill: {cents(s.creditCents)}
                  </Radio>
                  <Radio>
                    <input type="radio" checked={settlement === "refund"} onChange={() => setSettlement("refund")} />
                    Refund to their card: {cents(s.refundCents)}
                  </Radio>
                </>
              )}
            </FormField>
          )}

          {preview.checkedIn && (
            <Radio style={{ marginBottom: 12 }}>
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
              I know they've already been checked in this period and want to move them anyway.
            </Radio>
          )}

          <Row>
            <Button onClick={confirmMove} disabled={!canConfirm}>
              {moveLoading ? "Updating Stripe…" : "Confirm change"}
            </Button>
          </Row>
        </>
      )}
      {moveStatus && <StatusText success={moveStatus.ok}>{moveStatus.msg}</StatusText>}

      <SectionTitle style={{ fontSize: 14, marginTop: 24, marginBottom: 12 }}>Pause</SectionTitle>
      {pausedUntil ? (
        <>
          <Box>
            {db?.current
              ? `Pause scheduled. They keep access until their period ends, then renew on ${date(pausedUntil)}.`
              : `Paused. Their membership resumes on ${date(pausedUntil)}.`}
          </Box>
          <Row>
            {db?.current ? (
              <Button variant="secondary" onClick={() => runPause("undo")} disabled={pauseLoading}>
                Undo scheduled pause
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => runPause("resume")} disabled={pauseLoading}>
                Resume now (charges today)
              </Button>
            )}
          </Row>
        </>
      ) : (
        <Row>
          <Select value={pauseDays} onChange={(e) => setPauseDays(Number(e.target.value) as 7 | 14 | 30)} style={{ maxWidth: 200 }}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </Select>
          <Button variant="secondary" onClick={() => runPause("pause")} disabled={pauseLoading || !stripe}>
            Pause membership
          </Button>
        </Row>
      )}
      {pauseStatus && <StatusText success={pauseStatus.ok}>{pauseStatus.msg}</StatusText>}
    </>
  );
};

export default MemberMembership;
